/**
 * The identity workbench's pure model — no React, no JSX, no network.
 *
 * FD-507 lifted this out of `IdentityMappingsSection.tsx`, which had reached
 * 1854 lines and could not absorb the queue, the inspector or the bulk review
 * R-FD-053 adds. Nothing here changed in the move: every type, precedence rule
 * and helper is the one that shipped, with its reasoning intact, so the 1675
 * lines of tests over this behaviour pass unedited.
 */
import { normalizeSid } from '../../api/identity';
import {
  D2CMappingSubmission,
  IdentityMappingView,
  IdentitySuggestionUser,
  MappingSubmission,
  SuggestionCandidate,
  SuggestionMatchedOn,
  TenantUser,
} from '../../types/identity';
import { MappingChipStatus, deriveMappingChipStatus } from './MappingStatusChip';

// ---------------------------------------------------------------------------
// Staged-edits model (FD-098). Accepts, picker choices, and removals
// accumulate locally; nothing writes until the admin clicks Save. Submits go
// through set-identity-mappings (per-type row shapes, FD-103 v1.1) and
// remove-identity-mappings (sourceKeys). Match verdicts, validation, and
// consent decisions come from the API verbatim — no business logic here.
// ---------------------------------------------------------------------------

export type StagedEdit =
  | {
      kind: 'set';
      label: string;
      submission: MappingSubmission;
      destUser: TenantUser;
      // The tier that produced this candidate, carried so the Match type column
      // keeps saying WHY the destination is what it is after an approve stages
      // it. Absent on a manual add and on a picker Change — those are operator
      // choices, not matches, and the column shows an em-dash rather than
      // borrowing a tier from a suggestion the operator overrode.
      matchedOn?: SuggestionMatchedOn;
    }
  | { kind: 'remove'; label: string; sourceKey: string };

export type RowVM =
  | { kind: 'suggestion'; key: string; row: IdentitySuggestionUser }
  // FD-387 — a mapping that exists on the server with no device behind it, so
  // the suggestions feed has no row to hang it on. Written successfully and
  // previously invisible.
  | { kind: 'mapping'; key: string; mapping: IdentityMappingView }
  | {
      kind: 'manual';
      key: string;
      sourceUser: TenantUser;
      staged: Extract<StagedEdit, { kind: 'set' }>;
    };

// FD-381 — `addSource`/`addDestination` are gone. They modelled the two-step
// dialog sequence: open the picker for the source tenant, close it, reopen it
// for the destination. The inline two-panel add replaced that with one surface
// on which both choices are visible at once, so only the row-level Change
// dialog remains a picker.

// FD-384 — the row filter. Deliberately built on `deriveMappingChipStatus`'s
// vocabulary rather than a second one: the chip an operator reads in the Status
// column and the chip they click to filter by must mean the same thing, or the
// filter teaches a different model of the data than the table does.
//
// These are VIEWS, not a partition — `notReady` is a subset of `mapped`, and
// `action` spans three states — so the counts deliberately do not sum to the
// total. What is asserted instead is that each chip's count equals the number
// of rows it actually shows, which is the property an operator relies on.
export type RowFilter = 'action' | 'all' | 'suggested' | 'mapped' | 'notReady' | 'attention';

export const FILTER_LABELS: Record<RowFilter, string> = {
  action: 'Needs action',
  all: 'All',
  suggested: 'Suggested',
  mapped: 'Mapped',
  notReady: 'Destination not ready',
  attention: 'Needs attention',
};

export const FILTER_ORDER: RowFilter[] = [
  'action',
  'suggested',
  'attention',
  'notReady',
  'mapped',
  'all',
];

export function matchesFilter(row: IdentitySuggestionUser, filter: RowFilter): boolean {
  const status = deriveMappingChipStatus(row);
  const destinationNotReady =
    Boolean(row.existingMapping) && row.existingMapping?.destinationReady === false;
  switch (filter) {
    case 'all':
      return true;
    case 'suggested':
      return status === 'suggested';
    case 'mapped':
      return status === 'mapped';
    case 'attention':
      return status === 'needsAttention';
    case 'notReady':
      return destinationNotReady;
    case 'action':
      // Everything an operator still has to do something about: unmapped,
      // suggested-but-unapproved, needing attention, or mapped to a destination
      // the delivery gate will not accept (FD-ADR-036).
      return status !== 'mapped' || destinationNotReady;
  }
}

// FD-495 — the same filter, lifted to the RowVM so it can be applied AFTER the
// row universe is built rather than during construction. Behaviour is
// unchanged, kind for kind:
//   suggestion → the chip-vocabulary filter above
//   manual     → always visible; a staged row the operator just created must
//                not disappear behind the filter that was active when they
//                created it
//   mapping    → `mapped` by definition, with the one actionable exception: a
//                mapping whose destination is not ready holds a device at
//                409 IDENTITY_NOT_READY, so it belongs in `Needs action` too
export function matchesRowFilter(vm: RowVM, filter: RowFilter): boolean {
  if (vm.kind === 'suggestion') return matchesFilter(vm.row, filter);
  if (vm.kind === 'manual') return true;
  const notReady = vm.mapping.destinationReady === false;
  switch (filter) {
    case 'all':
    case 'mapped':
      return true;
    case 'notReady':
    case 'action':
      return notReady;
    default:
      return false;
  }
}

export const MATCHED_ON_LABELS: Record<SuggestionMatchedOn, string> = {
  sidExact: 'SID exact',
  samAccountName: 'SAM',
  userPrincipalName: 'UPN',
  mail: 'mail',
  mailNickname: 'mail nickname',
};

export const NEEDS_ATTENTION_COPY: Record<string, string> = {
  unsupportedSidFormat: 'Unsupported SID format',
  sourceNotResolved: 'Source identity not resolved',
};

export function rowLabel(row: IdentitySuggestionUser): string {
  return row.displayName ?? row.samName ?? row.userName ?? row.sid;
}

export function truncateSid(sid: string): string {
  return sid.length <= 20 ? sid : `${sid.slice(0, 19)}…`;
}

// Key a submission the way the server echoes normalized row keys (FD-103
// v1.1): lowercase source objectId GUID for T2T; uppercase-S SID for D2C.
export function submissionKey(submission: MappingSubmission): string {
  if ('sourceUserId' in submission && submission.sourceUserId) {
    return submission.sourceUserId.toLowerCase();
  }
  return normalizeSid((submission as D2CMappingSubmission).sourceUserSid);
}

export function normalizeEchoKey(key: string): string {
  return /^[Ss]-1-/.test(key) ? normalizeSid(key) : key.toLowerCase();
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}

/**
 * The source side of a row, flattened to the three things the table and the
 * drawer both want to name it by, per row kind.
 *
 * `upn` for a suggestion row prefers the Graph-RESOLVED source user over the
 * device-reported `userName`: the device's value is whatever Windows had for
 * the last interactive logon, while `resolvedSourceUser` is the source tenant's
 * own answer (and is null on D2C, which has no source tenant — FD-ADR-029).
 */
export function sourceIdentity(vm: RowVM): {
  name: string | null;
  upn: string | null;
  sid: string | null;
} {
  if (vm.kind === 'manual') {
    const u = vm.sourceUser;
    return {
      name: u.displayName,
      upn: u.userPrincipalName,
      sid: u.onPremisesSecurityIdentifier,
    };
  }
  if (vm.kind === 'mapping') {
    const m = vm.mapping;
    return {
      name: m.sourceDisplayName,
      upn: m.sourceUserPrincipalName,
      sid: m.sourceUserSid ?? m.sourceUserId,
    };
  }
  const { row } = vm;
  return {
    name: row.displayName ?? row.samName ?? null,
    upn: row.resolvedSourceUser?.userPrincipalName ?? row.userName ?? null,
    sid: row.sid,
  };
}

/**
 * What the destination side of a row currently says — resolved ONCE and read by
 * both the Destination user column and the Match type column beside it.
 *
 * The two columns are two views of one answer, so they are derived from one
 * function rather than each re-deriving it: a Match type that could disagree
 * with the destination it annotates is worse than no Match type at all.
 */
export type DestinationView =
  | {
      kind: 'staged-set';
      displayName: string | null;
      upn: string | null;
      matchedOn?: SuggestionMatchedOn;
    }
  | { kind: 'staged-removal' }
  | { kind: 'mapped'; displayName: string | null; upn: string | null; notReady: boolean }
  | {
      kind: 'candidate';
      displayName: string | null;
      upn: string | null;
      matchedOn: SuggestionMatchedOn;
      /** FD-511 — `accountEnabled === false` on the suggested destination. */
      disabled: boolean;
      alternates: SuggestionCandidate[];
      row: IdentitySuggestionUser;
    }
  | { kind: 'needs-attention'; message: string }
  | { kind: 'none' };

export function resolveDestination(
  vm: RowVM,
  staged: Map<string, StagedEdit>,
): DestinationView {
  if (vm.kind === 'manual') {
    return {
      kind: 'staged-set',
      displayName: vm.staged.destUser.displayName,
      upn: vm.staged.destUser.userPrincipalName,
      matchedOn: vm.staged.matchedOn,
    };
  }

  const edit = staged.get(vm.key);
  if (edit?.kind === 'set') {
    return {
      kind: 'staged-set',
      displayName: edit.destUser.displayName ?? null,
      upn: edit.destUser.userPrincipalName ?? edit.destUser.id,
      matchedOn: edit.matchedOn,
    };
  }
  if (edit?.kind === 'remove') return { kind: 'staged-removal' };

  if (vm.kind === 'mapping') {
    const m = vm.mapping;
    return {
      kind: 'mapped',
      displayName: m.destDisplayName,
      upn: m.destUserPrincipalName ?? m.destUserId,
      notReady: m.destinationReady === false,
    };
  }

  const { row } = vm;
  if (row.existingMapping) {
    const m = row.existingMapping;
    return {
      kind: 'mapped',
      displayName: m.destDisplayName,
      upn: m.destUserPrincipalName ?? m.destUserId,
      notReady: m.destinationReady === false,
    };
  }
  const candidate = row.suggestions[0];
  if (candidate) {
    return {
      kind: 'candidate',
      displayName: candidate.user.displayName,
      upn: candidate.user.userPrincipalName ?? candidate.user.id,
      matchedOn: candidate.matchedOn,
      disabled: candidate.user.accountEnabled === false,
      alternates: row.suggestions.slice(1),
      row,
    };
  }
  if (row.needsAttention) {
    return {
      kind: 'needs-attention',
      message:
        (row.needsAttentionReason && NEEDS_ATTENTION_COPY[row.needsAttentionReason]) ??
        row.needsAttentionReason ??
        'Needs attention',
    };
  }
  return { kind: 'none' };
}


// ---------------------------------------------------------------------------
// What a row lets an operator DO — derived once, rendered twice (FD-509).
//
// The FD-ADR-036 freeze has THREE different server behaviours hanging off one
// predicate: adding a mapping to an unmapped row is allowed while a wave is
// active, changing an existing one is a per-row reject inside a 207, and
// removal is a WHOLE-REQUEST 409. FD-ADR-037 §4 makes presenting that a single
// obligation, and FD-495 declined to give the drawer actions at all precisely
// because a second set of buttons would be a second implementation of it.
//
// This function is the answer to that objection rather than an override of it.
// The row's action cluster and the inspector's both render from ONE derivation,
// so they cannot disagree about what the server will accept — and the reason
// strings live here too, because two surfaces explaining the same refusal in
// two different sentences is the same defect wearing better clothes.
// ---------------------------------------------------------------------------

export const CHANGE_DISABLED_SOURCE_UNRESOLVED =
  'Source identity unresolved — use Add mapping to map this user manually.';
export const CHANGE_DISABLED_FROZEN =
  'This wave is active, so an existing mapping cannot be changed. A mapping can still be added for an identity that has none.';

export type RowActions =
  /** A staged edit is pending on this row — Undo is the only action. */
  | { kind: 'staged' }
  /**
   * FD-387 — a saved mapping with no suggestions row behind it. There is
   * nothing to Change against, but it can be removed, which is how an orphaned
   * mapping is cleaned up. Withheld while frozen, per the whole-request 409.
   */
  | { kind: 'orphanMapping'; removable: boolean; removeLabel: string; removeSourceKey: string }
  | {
      kind: 'suggestion';
      row: IdentitySuggestionUser;
      candidate: SuggestionCandidate | null;
      isMapped: boolean;
      sourceUnresolved: boolean;
      /** Offered only where approving is an ADD (FD-382). */
      approvable: boolean;
      changeDisabled: boolean;
      changeDisabledReason?: string;
      /** Withheld, never disabled — a staged removal fails the whole save. */
      removable: boolean;
    }
  | { kind: 'none' };

export function deriveRowActions(
  vm: RowVM,
  staged: Map<string, StagedEdit>,
  options: { frozen: boolean; isT2T: boolean },
): RowActions {
  const { frozen, isT2T } = options;
  const edit = staged.get(vm.key);

  if (vm.kind === 'mapping' && !edit) {
    return {
      kind: 'orphanMapping',
      removable: !frozen,
      removeLabel:
        vm.mapping.sourceDisplayName ??
        vm.mapping.sourceUserPrincipalName ??
        vm.mapping.sourceUserId ??
        vm.key,
      removeSourceKey: vm.mapping.sourceUserId ?? vm.mapping.sourceUserSid ?? '',
    };
  }
  if (edit || vm.kind === 'manual') return { kind: 'staged' };
  if (vm.kind !== 'suggestion') return { kind: 'none' };

  const { row } = vm;
  const sourceUnresolved = isT2T && !row.resolvedSourceUser;
  const candidate = row.suggestions[0] ?? null;
  const isMapped = Boolean(row.existingMapping);

  // Changing a MAPPED row while the wave is active is an update, which the
  // server rejects per-row. Changing an UNMAPPED row is an add, which it
  // allows — so the freeze disables one and not the other.
  const changeIsFrozenUpdate = frozen && isMapped;
  const changeDisabled = sourceUnresolved || changeIsFrozenUpdate;

  return {
    kind: 'suggestion',
    row,
    candidate,
    isMapped,
    sourceUnresolved,
    approvable: !isMapped && Boolean(candidate) && !sourceUnresolved,
    changeDisabled,
    changeDisabledReason: sourceUnresolved
      ? CHANGE_DISABLED_SOURCE_UNRESOLVED
      : changeIsFrozenUpdate
        ? CHANGE_DISABLED_FROZEN
        : undefined,
    removable: isMapped && !frozen,
  };
}

// ---------------------------------------------------------------------------
// The inspector's view model. A pure shape, so the hook that builds it and the
// component that renders it do not have to import each other.
// ---------------------------------------------------------------------------

/** Which identity the inspector is about, plus everything known about it. */
export interface IdentityDetail {
  /** RowVM key — the normalized SID, `mapping:<key>`, or `manual:<objectId>`. */
  key: string;
  title: string;
  status: MappingChipStatus;
  /**
   * The suggestions-feed row, when one exists. Absent for a saved mapping that
   * no device reported (FD-387) — which is exactly why that row exists.
   */
  row?: IdentitySuggestionUser | null;
  /** The saved mapping, from the row or from the mappings read. */
  mapping?: IdentityMappingView | null;
  /** A staged, unsaved destination — nothing is written until Save (FD-098). */
  stagedDestination?: { displayName: string | null; upn: string | null; id: string } | null;
  /** True when the staged edit is a removal rather than a set. */
  stagedRemoval?: boolean;
  serialNumbers: string[];
  deviceCount: number;
}


// ---------------------------------------------------------------------------
// Why a row needs attention — FOUR reasons, and every one of them has a
// producer (FD-511).
//
// The mockups draw six: no matching user, multiple matches, attribute mismatch,
// ambiguous identity, inactive destination user, domain mismatch. The server
// emits exactly TWO reason codes — `unsupportedSidFormat` and
// `sourceNotResolved` — and computes no ambiguity, no attribute comparison and
// no tenant/domain comparison anywhere. Two more facts ARE on the wire and are
// worth naming: a mapping the delivery gate will refuse (`destinationReady`,
// FD-ADR-036's own predicate) and a destination account that is disabled
// (`accountEnabled`, one of the four `autoAcceptEligible` conjuncts).
//
// MULTIPLE MATCHES IS DELIBERATELY NOT HERE, and that is the refusal most
// likely to be re-proposed. The server returns up to five ranked candidates and
// never computes ambiguity; the candidate COUNT is a visible fact and already
// lives in the inspector's chooser. A "Multiple matches" reason would be a
// verdict nothing produced — and a reason restating "Destination user = —"
// would be the restatement FD-504 removed from the check-in cell.
// ---------------------------------------------------------------------------

export type ExceptionReasonCode =
  | 'unsupportedSidFormat'
  | 'sourceNotResolved'
  | 'destinationNotReady'
  | 'destinationDisabled';

export const EXCEPTION_REASON_LABELS: Record<ExceptionReasonCode, string> = {
  unsupportedSidFormat: 'Unsupported SID format',
  sourceNotResolved: 'Source user not found in the source tenant',
  destinationNotReady: 'Destination not ready',
  destinationDisabled: 'Destination account is disabled',
};

export const EXCEPTION_REASON_DETAIL: Record<ExceptionReasonCode, string> = {
  unsupportedSidFormat: 'The device reported a SID this platform cannot classify.',
  sourceNotResolved: 'The source directory returned no user for this SID.',
  destinationNotReady:
    'The mapping exists but its destination is not a valid object ID, so the delivery gate holds every device behind it.',
  destinationDisabled:
    'The suggested destination account is disabled. Approving it is allowed; the sign-in will not work until it is enabled.',
};

/**
 * The row's exception, or null. Precedence is "what stops this row first":
 * an unresolvable source before an unusable destination, and a mapping the gate
 * will refuse before a candidate that merely has a problem.
 */
export function deriveExceptionReason(vm: RowVM): ExceptionReasonCode | null {
  if (vm.kind === 'mapping') {
    return vm.mapping.destinationReady === false ? 'destinationNotReady' : null;
  }
  if (vm.kind !== 'suggestion') return null;
  const { row } = vm;

  if (row.needsAttention) {
    // The server's own code when it sent one. An unrecognised value is NOT
    // coerced into one of ours — it returns null, and the row keeps its chip
    // without prose we cannot justify.
    if (
      row.needsAttentionReason === 'unsupportedSidFormat' ||
      row.needsAttentionReason === 'sourceNotResolved'
    ) {
      return row.needsAttentionReason;
    }
    return null;
  }
  if (row.existingMapping?.destinationReady === false) return 'destinationNotReady';
  if (row.suggestions[0]?.user.accountEnabled === false) return 'destinationDisabled';
  return null;
}

// ---------------------------------------------------------------------------
// FD-513 — the loaded queue as CSV.
//
// IT EXPORTS WHAT THE TABLE SHOWS, and its filename says so. The suggestions
// feed PAGES (50 users at a time, "Load more" below the table), so an export
// taken here covers the rows currently loaded and filtered — not the campaign.
// Naming the file `…-loaded.csv` rather than `…-identities.csv` is the whole
// mitigation: a spreadsheet outlives the screen it came from, and a file called
// "identities" is one somebody will later treat as the campaign's full set.
//
// This is deliberately NOT the FD-097 round-trip template. That is a SERVER
// artifact (`getIdentityMappingTemplate`) whose columns are the import
// contract; this is a view of the queue, columns and all, including staged
// edits and exception reasons the server has never seen. Conflating them would
// produce a file that looks importable and is not.
// ---------------------------------------------------------------------------

/** One CSV field, RFC 4180: quote when it must be quoted, double inner quotes. */
function csvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export const IDENTITY_QUEUE_CSV_HEADER = [
  'Source user',
  'Source UPN',
  'Source SID',
  'Destination user',
  'Destination UPN',
  'Match type',
  'State',
  'Exception',
  'Devices',
  'Pending change',
] as const;

const DESTINATION_STATE: Record<DestinationView['kind'], string> = {
  'staged-set': 'Pending approval',
  'staged-removal': 'Pending removal',
  mapped: 'Mapped',
  candidate: 'Suggested',
  'needs-attention': 'Needs attention',
  none: 'Unmapped',
};

export function identityQueueToCsv(
  rows: RowVM[],
  staged: Map<string, StagedEdit>,
  deviceCountFor: (vm: RowVM) => number,
): string {
  const lines = rows.map((vm) => {
    const source = sourceIdentity(vm);
    const dest = resolveDestination(vm, staged);
    const reason = deriveExceptionReason(vm);
    // A staged edit is EXPORTED AS PENDING, never as fact. The queue is a
    // review surface and nothing is written until Save, so a spreadsheet that
    // presented a staged approval as a saved mapping would assert something
    // the server has not been told.
    const pending = staged.has(vm.key)
      ? staged.get(vm.key)?.kind === 'remove'
        ? 'remove'
        : 'set'
      : '';
    return [
      source.name ?? '',
      source.upn ?? '',
      source.sid ?? '',
      'displayName' in dest ? dest.displayName ?? '' : '',
      'upn' in dest ? dest.upn ?? '' : '',
      'matchedOn' in dest && dest.matchedOn
        ? MATCHED_ON_LABELS[dest.matchedOn] ?? dest.matchedOn
        : '',
      DESTINATION_STATE[dest.kind] ?? dest.kind,
      reason ? EXCEPTION_REASON_LABELS[reason] : '',
      String(deviceCountFor(vm)),
      pending,
    ]
      .map(csvField)
      .join(',');
  });
  return [IDENTITY_QUEUE_CSV_HEADER.join(','), ...lines].join('\r\n');
}
